import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
/* eslint import/no-webpack-loader-syntax: off */
import mapboxgl from "!mapbox-gl";
import html2canvas from "html2canvas";
import { Switch, Button } from "antd";

mapboxgl.accessToken =
  "pk.eyJ1IjoibGt0eWFnaSIsImEiOiJja2dhMmVrNGcwMzNxMnRrenhrbGh4YzBoIn0.h31ayIFQsUoW1YpL3LVkOQ";

const ClusterMap = forwardRef(({ data, hazard, height = 650 }, ref) => {
  const worldmapContainer = useRef(null);
  const mapRef = useRef(null);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/light-v10");
  const [isExporting, setIsExporting] = useState(false);

  const address = data?.[0]?.location?.name ?? "map";

  // -------------------------------------------------------
  //  METHOD 1: Direct Canvas Export (Simpler, Works Better)
  // -------------------------------------------------------
  const handleExportImage = async () => {
    const map = mapRef.current;

    if (!map) {
      console.error("❌ Map not ready");
      return;
    }

    setIsExporting(true);

    try {
      // Wait for map to be idle (all tiles loaded)
      await new Promise((resolve) => {
        if (map.loaded()) {
          resolve();
        } else {
          map.once("idle", resolve);
        }
      });

      // Get the canvas directly from Mapbox
      const canvas = map.getCanvas();

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `risk_map_${address}_${Date.now()}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);

        console.log("✅ Map exported successfully");
        setIsExporting(false);
      }, "image/png");
    } catch (error) {
      console.error("❌ Export failed:", error);
      alert("Failed to export map. Please try again.");
      setIsExporting(false);
    }
  };

  // -------------------------------------------------------
  //  METHOD 2: High-Resolution Export with html2canvas
  // -------------------------------------------------------
  const handleExportImageHD = async () => {
    const container = worldmapContainer.current;
    const map = mapRef.current;

    if (!container || !map) {
      console.error("❌ Map or container missing");
      return;
    }

    setIsExporting(true);

    try {
      // Hide UI controls temporarily
      const controls = container.querySelectorAll(
        ".mapboxgl-ctrl-top-right, .mapboxgl-ctrl-bottom-right, .mapboxgl-ctrl-bottom-left"
      );
      controls.forEach((ctrl) => (ctrl.style.display = "none"));

      // Wait for map to finish loading
      await new Promise((resolve) => {
        if (map.loaded()) {
          resolve();
        } else {
          map.once("idle", resolve);
        }
      });

      // Small delay for stability
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Capture with html2canvas
      const canvas = await html2canvas(container, {
        useCORS: true,
        allowTaint: false,
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 0,
      });

      // Download
      const link = document.createElement("a");
      link.download = `risk_map_HD_${address}_${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();

      console.log("✅ HD Map exported successfully");

      // Restore UI controls
      controls.forEach((ctrl) => (ctrl.style.display = ""));
    } catch (error) {
      console.error("❌ Export failed:", error);
      alert("Failed to export map. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Expose to parent component
  useImperativeHandle(ref, () => ({
    downloadMap: handleExportImage,
    downloadMapHD: handleExportImageHD,
  }));

  // -------------------------------------------------------
  //  MAP INITIALIZATION
  // -------------------------------------------------------
  useEffect(() => {
    if (!worldmapContainer.current || !data) return;

    const map = new mapboxgl.Map({
      container: worldmapContainer.current,
      style: mapStyle,
      center: [data[0].location.longitude, data[0].location.latitude],
      zoom: 2,
      scrollZoom: true,
      preserveDrawingBuffer: true, // CRITICAL for canvas export
    });

    mapRef.current = map;

    map.on("load", () => {
      setTimeout(() => map.resize(), 200);
    });

    const features = data.map((item, i) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [item.location.longitude, item.location.latitude],
      },
      properties: {
        id: i,
        name: item.location.name,
        total_loss: item.data.total_loss,
        percentage: item.data.percentage,
        top_hazard_string: item.data.top_hazard_string,
        hazard_score: item.data[hazard],
      },
    }));

    const stores = { type: "FeatureCollection", features };

    stores.features.forEach((store) => {
      const h = store.properties.hazard_score;
      let color = "#fdffe5";
      if (h >= 100) color = "#a4222b";
      else if (h > 76) color = "#c92128";
      else if (h > 51) color = "#ffae3a";
      else if (h > 26) color = "#fce97f";
      else if (h > 1) color = "#f9ffb8";
      store.properties["marker-color"] = color;
    });

    map.on("load", () => {
      stores.features.forEach((marker) => {
        const el = document.createElement("div");
        el.className = "worldmarker";
        el.style.backgroundColor = marker.properties["marker-color"];
        el.style.border = "2px solid #000";
        el.style.width = "12px";
        el.style.height = "12px";
        el.style.borderRadius = "50%";
        el.style.cursor = "pointer";

        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 15,
        });

        const {
          name,
          total_loss,
          percentage,
          top_hazard_string,
          hazard_score,
        } = marker.properties;

        el.addEventListener("mouseenter", () => {
          popup
            .setLngLat(marker.geometry.coordinates)
            .setHTML(
              `
              <div style="font-family:Inter, sans-serif; min-width:180px;">
                <div style="font-weight:600; margin-bottom:4px; color:#000;">${name}</div>
                <div style="font-size:12px; color:#555;">
                  <b>Total Loss:</b> $${total_loss.toFixed(2)}M <br/>
                  <b>Loss (%):</b> ${percentage.toFixed(2)}% <br/>
                  <b>Top Risk:</b> ${top_hazard_string} <br/>
                  <b>${hazard} Score:</b> ${hazard_score.toFixed(2)}
                </div>
              </div>
            `
            )
            .addTo(map);
        });

        el.addEventListener("mouseleave", () => popup.remove());

        new mapboxgl.Marker(el)
          .setLngLat(marker.geometry.coordinates)
          .addTo(map);
      });
    });

    return () => map.remove();
  }, [data, hazard]);

  // -------------------------------------------------------
  //  HANDLE STYLE SWITCH
  // -------------------------------------------------------
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setStyle(mapStyle);
      mapRef.current.once("style.load", () => {
        mapRef.current.resize();
      });
    }
  }, [mapStyle]);

  // -------------------------------------------------------
  //  RENDER UI
  // -------------------------------------------------------
  return (
    <div style={{ position: "relative" }}>
      {/* View Switch (LEFT) */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 999,
          background: "white",
          padding: "6px 12px",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <span>View:</span>
        <Switch
          checkedChildren="Satellite"
          unCheckedChildren="Light"
          onChange={(checked) =>
            setMapStyle(
              checked
                ? "mapbox://styles/mapbox/satellite-streets-v11"
                : "mapbox://styles/mapbox/light-v10"
            )
          }
        />
      </div>

      {/* Download Buttons (RIGHT) */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 999,
          display: "flex",
          gap: "8px",
        }}
      >
        <Button onClick={handleExportImageHD} loading={isExporting}>
          Download
        </Button>
      </div>

      {/* MAP */}
      <div
        ref={worldmapContainer}
        className="map-container2"
        style={{ width: "100%", height: "80vh" }}
      />
    </div>
  );
});

export default ClusterMap;
